/**
 * SearchingScreen.tsx
 * Opponent search screen
 */

import { type FC, useState, useEffect } from 'react';

import { Button, Icon, TextBlock, Title } from '@skillarea/lib-frontend';
import type { GameReturnDto } from '@skillarea/lib-shared';
import { useTranslation } from 'react-i18next';
import { useAdaptive } from '@skillarea/lib-frontend';

import styles from '../MatchmakingModal.module.scss';
import { formatCountdownMs } from '../utils/formatCountdownMs';

import { useLocalizedGame } from '~/hooks/useLocalizedGame';

export const SearchingScreen: FC<{
  game: GameReturnDto;
  initialElapsedTimeSeconds?: number;
  longWaitMessage?: string;
  longWaitThresholdSeconds?: number;
  message?: string;
  onCancel?: () => void;
  isCancelling?: boolean;
  countdownMs?: number;
}> = ({
  countdownMs,
  game,
  initialElapsedTimeSeconds = 0,
  isCancelling = false,
  longWaitMessage,
  longWaitThresholdSeconds = 120,
  message,
  onCancel,
}) => {
  const { t } = useTranslation('matchmaking');
  const localizedGame = useLocalizedGame(game);
  const { isMobile } = useAdaptive();
  const [elapsedTime, setElapsedTime] = useState(initialElapsedTimeSeconds);
  const watchIconSize = isMobile ? 104 : 142;
  const defaultMessage = t('search.defaultMessage');

  useEffect(() => {
    setElapsedTime(initialElapsedTimeSeconds);
  }, [initialElapsedTimeSeconds]);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const displayedMessage =
    longWaitMessage && elapsedTime > longWaitThresholdSeconds
      ? longWaitMessage
      : (message ?? defaultMessage);

  const timerText =
    typeof countdownMs === 'number'
      ? formatCountdownMs(countdownMs)
      : formatCountdownMs(elapsedTime * 1000);

  return (
    <div className={styles.screen} data-testid="searching-screen">
      <div className={styles.centered}>
        <Title
          className={styles.screenTitle}
          color="contrast"
          size="l"
          variant="benzin"
        >
          {localizedGame.name}
        </Title>

        <div className={styles.spinnerContainer}>
          <Icon img="watch" size={watchIconSize} type="colored" />
        </div>

        <TextBlock
          className={styles.searchTimer}
          color="accent"
          size="xl"
          weight="600"
        >
          {timerText}
        </TextBlock>

        <TextBlock className={styles.searchingText} color="accent">
          {displayedMessage}
        </TextBlock>
      </div>

      {onCancel && (
        <div className={styles.footer}>
          <div className={styles.actionButtons}>
            <Button
              className={styles.actionButton}
              color="primary"
              disabled={isCancelling}
              name={t('search.cancelSearch')}
              onClick={onCancel}
              size="s"
              variant="filled"
            >
              {t('search.cancel')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
